import SentryService from '@services/SentryService';

import type { IStorageBackend, WorkerRequest, WorkerResponse } from './types';

const REQUEST_TIMEOUT_MS = 30_000;

interface PendingRequest {
  resolve: (value: WorkerResponse) => void;
  reject: (reason: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Main-thread coordinator that talks to a Dedicated Worker running OPFS I/O.
 *
 * The Worker is a dumb I/O executor — all Sentry logging, context enrichment,
 * and error handling happens here on the main thread.
 */
export class OPFSStorageBackend implements IStorageBackend {
  private worker: Worker | null = null;
  private pending = new Map<number, PendingRequest>();
  private nextId = 1;

  async initialize(): Promise<void> {
    SentryService.addBreadcrumb({
      category: 'storage.opfs',
      message: 'Spawning OPFS Worker',
      level: 'info',
    });

    this.worker = new Worker(new URL('./storage.worker.ts', import.meta.url), { type: 'module' });

    this.worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const res = event.data;
      const pending = this.pending.get(res.id);
      if (!pending) return;

      clearTimeout(pending.timer);
      this.pending.delete(res.id);
      pending.resolve(res);
    };

    this.worker.onerror = (event: ErrorEvent) => {
      SentryService.captureException(new Error(`OPFS Worker error: ${event.message}`));
    };

    // Verify the worker is alive
    const pong = await this.send({ type: 'ping', id: this.nextId++ });
    if (pong.type !== 'pong') {
      throw new Error('OPFS Worker failed ping handshake');
    }

    SentryService.addBreadcrumb({
      category: 'storage.opfs',
      message: 'OPFS Worker ready',
      level: 'info',
    });
  }

  async writeChunk(sessionId: string, chunkIndex: number, data: ArrayBuffer): Promise<void> {
    const span = SentryService.startInactiveSpan(
      'storage.opfs.write',
      `${sessionId}/${chunkIndex}`,
    );

    const copy = data.slice(0);
    const res = await this.send(
      { type: 'writeChunk', id: this.nextId++, sessionId, chunkIndex, data: copy },
      [copy],
    );

    span?.end();

    if (res.type === 'error') {
      throw new Error(`OPFS write failed: ${res.message}`);
    }
  }

  async readChunk(sessionId: string, chunkIndex: number): Promise<ArrayBuffer | null> {
    const res = await this.send({
      type: 'readChunk',
      id: this.nextId++,
      sessionId,
      chunkIndex,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS read failed: ${res.message}`);
    }

    if (res.type === 'data') {
      return res.data ?? null;
    }

    return null;
  }

  async hasChunk(sessionId: string, chunkIndex: number): Promise<boolean> {
    const res = await this.send({
      type: 'hasChunk',
      id: this.nextId++,
      sessionId,
      chunkIndex,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS hasChunk failed: ${res.message}`);
    }

    return res.type === 'exists' ? res.exists : false;
  }

  async deleteChunk(sessionId: string, chunkIndex: number): Promise<void> {
    const res = await this.send({
      type: 'deleteChunk',
      id: this.nextId++,
      sessionId,
      chunkIndex,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS delete failed: ${res.message}`);
    }
  }

  async listChunks(sessionId: string): Promise<number[]> {
    const res = await this.send({
      type: 'listChunks',
      id: this.nextId++,
      sessionId,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS listChunks failed: ${res.message}`);
    }

    if (res.type === 'list') {
      return res.indices;
    }

    return [];
  }

  async deleteSession(sessionId: string): Promise<void> {
    const res = await this.send({
      type: 'deleteSession',
      id: this.nextId++,
      sessionId,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS deleteSession failed: ${res.message}`);
    }

    SentryService.addBreadcrumb({
      category: 'storage.opfs',
      message: `Deleted session ${sessionId}`,
      level: 'info',
    });
  }

  async listSessions(): Promise<string[]> {
    const res = await this.send({
      type: 'listSessions',
      id: this.nextId++,
    });

    if (res.type === 'error') {
      throw new Error(`OPFS listSessions failed: ${res.message}`);
    }

    if (res.type === 'sessions') {
      return res.sessionIds;
    }

    return [];
  }

  /** Terminate the Worker and reject all pending requests. */
  dispose(): void {
    for (const [id, pending] of this.pending) {
      clearTimeout(pending.timer);
      pending.reject(new Error('OPFSStorageBackend disposed'));
      this.pending.delete(id);
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  // ── Private ──

  private send(req: WorkerRequest, transfer?: Transferable[]): Promise<WorkerResponse> {
    const worker = this.worker;
    if (!worker) {
      return Promise.reject(new Error('OPFSStorageBackend not initialized'));
    }

    return new Promise<WorkerResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(req.id);
        const err = new Error(`OPFS Worker request timed out (${req.type}, id=${req.id})`);
        SentryService.captureException(err);
        reject(err);
      }, REQUEST_TIMEOUT_MS);

      this.pending.set(req.id, { resolve, reject, timer });

      if (transfer) {
        worker.postMessage(req, transfer);
      } else {
        worker.postMessage(req);
      }
    });
  }
}
